import { apiService } from './api.service';

// Interface for Telemetry user properties
interface TelemetryUserProperties {
  userId?: string;
  email?: string;
  name?: string;
  tenantId?: string;
  teamId?: string | null;
  isAdmin?: boolean;
  isInTeams?: boolean;
  isDevelopment?: boolean;
  [key: string]: any; // Allow additional properties
}

// Interface for Telemetry event options
interface TelemetryEventOptions {
  properties?: Record<string, any>;
  groups?: Record<string, string>;
}

/**
 * Telemetry Analytics Service
 * Handles user tracking and event logging via back-end API calls
 */
export class TelemetryService {
  private static instance: TelemetryService;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private debugMode = false;
  private telemetryEnabledCache: boolean | null = null;
  private telemetryEnabledCacheTime: number = 0;
  private readonly CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
  private silentMode = true; // Suppress console logs by default
  private sessionId: string | null = null;

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Get the singleton instance of TelemetryService
   */
  public static getInstance(): TelemetryService {
    if (!TelemetryService.instance) {
      TelemetryService.instance = new TelemetryService();
    }
    return TelemetryService.instance;
  }

  /**
   * Generate and store a session UUID.
   * Must be called once at app startup before any telemetry calls.
   * Uses crypto.randomUUID() which is available in all modern browsers.
   */
  public generateSessionId(): string {
    if (!this.sessionId) {
      this.sessionId = crypto.randomUUID();
      this.log('[TelemetryService] Session ID generated:', this.sessionId);
    }
    return this.sessionId;
  }

  /**
   * Enrich properties with $session_id if a session ID has been generated.
   */
  private injectSessionId(properties: Record<string, any>): Record<string, any> {
    if (!this.sessionId) return properties;
    return { ...properties, $session_id: this.sessionId };
  }

  /**
     * Log a message if not in silent mode
     */
   private log(message: string, ...args: any[]): void {
     if (!this.silentMode) {
       console.log(message, ...args);
     }
   }

  /**
    * Log an error if not in silent mode
    */
  private logError(message: string, ...args: any[]): void {
    if (!this.silentMode) {
      console.error(message, ...args);
    }
  }

  /**
    * Log a warning if not in silent mode
    */
  private logWarn(message: string, ...args: any[]): void {
    if (!this.silentMode) {
      console.warn(message, ...args);
    }
  }

   /**
     * Check if telemetry is enabled for the current user/tenant
     * Uses caching to avoid repeated API calls
     */
   private async isTelemetryEnabled(): Promise<boolean> {
     const now = Date.now();

     // Return cached value if still valid
     if (this.telemetryEnabledCache !== null && (now - this.telemetryEnabledCacheTime) < this.CACHE_DURATION_MS) {
       this.log('[TelemetryService] Using cached telemetry preference');
       return this.telemetryEnabledCache;
     }

     try {
        // Call backend API to check telemetry preference
        const data = await apiService.getTelemetryPreference();

       let enabled = true; // Default to true
       if (data.success && data.data !== undefined) {
         enabled = data.data.telemetryEnabled ?? true; // Default to true if undefined
       }

       // Cache the result
       this.telemetryEnabledCache = enabled;
       this.telemetryEnabledCacheTime = now;

       return enabled;
     } catch (error) {
       this.logError('[TelemetryService] Error checking telemetry preference:', error);
       // If API call fails, disable telemetry to respect user privacy
       return false;
     }
   }

  /**
    * Initialize telemetry service (no longer needs API key on front-end)
    */
  public initialize(debug: boolean = false): Promise<void> {
    if (this.initialized) {
      this.log('[TelemetryService] Already initialized');
      return Promise.resolve();
    }

    // Return the same promise if already in progress
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.debugMode = debug;
    // Set silent mode based on debug flag (silent by default, verbose in debug mode)
    this.silentMode = !debug;

    // Store the initialization promise so concurrent calls wait for the same operation
    this.initializationPromise = (async () => {
      try {
        this.log('[TelemetryService] Initializing telemetry service');

        // Check if back-end telemetry service is available
        const available = await this.checkStatus();

        if (available) {
          this.log('[TelemetryService] Back-end telemetry service is available');
          this.initialized = true;
        } else {
          this.logWarn('[TelemetryService] Back-end telemetry service is not available');
          this.initialized = false;
        }
      } catch (error) {
        this.logError('[TelemetryService] Error initializing telemetry service:', error);
        this.initialized = false;
      }
    })();

    return this.initializationPromise;
  }

   /**
     * Check if the back-end telemetry service is available
     */
    private async checkStatus(): Promise<boolean> {
      try {
        const data = await apiService.getTelemetryStatus();
        return data.success && data.data?.initialized;
      } catch (error) {
        this.logError('[TelemetryService] Error checking status:', error);
        return false;
      }
    }

   /**
     * Identify the current user
     */
   public async identify(userProperties: TelemetryUserProperties): Promise<void> {
     if (!this.initialized) {
       this.logWarn('[TelemetryService] Service not initialized, cannot identify user');
       return;
     }

     try {
       // Check if telemetry is enabled
       const telemetryEnabled = await this.isTelemetryEnabled();
       if (!telemetryEnabled) {
         this.log('[TelemetryService] Telemetry is disabled, skipping identify');
         return;
       }

        this.log('[TelemetryService] Identifying user:', userProperties);

        const result = await apiService.identifyTelemetryUser(userProperties, this.sessionId || undefined);
        if (!result.success) {
         throw new Error(result.error || 'Failed to identify user');
       }

       this.log('[TelemetryService] User identified successfully');
     } catch (error) {
       this.logError('[TelemetryService] Error identifying user:', error);
     }
   }

   /**
     * Capture an event
     */
   public async capture(eventName: string, distinctId: string, options: TelemetryEventOptions = {}): Promise<void> {
     if (!this.initialized) {
       this.logWarn('[TelemetryService] Service not initialized, cannot capture event:', eventName);
       return;
     }

     try {
       // Check if telemetry is enabled
       const telemetryEnabled = await this.isTelemetryEnabled();
       if (!telemetryEnabled) {
         this.log('[TelemetryService] Telemetry is disabled, skipping capture event:', eventName);
         return;
       }

       const { properties = {}, groups = {} } = options;
       const enrichedProperties = this.injectSessionId(properties);

        this.log('[TelemetryService] Capturing event:', eventName, { distinctId, properties: enrichedProperties, groups });

        const result = await apiService.captureTelemetryEvent(eventName, distinctId, enrichedProperties, groups);
        if (!result.success) {
         throw new Error(result.error || 'Failed to capture event');
       }

       this.log('[TelemetryService] Event captured successfully');
     } catch (error) {
       this.logError('[TelemetryService] Error capturing event:', error);
     }
   }

   /**
     * Track page view
     */
   public async trackPageView(pageName: string, distinctId: string, properties: Record<string, any> = {}): Promise<void> {
     if (!this.initialized) {
       this.logWarn('[TelemetryService] Service not initialized, cannot track page view:', pageName);
       return;
     }

     try {
       // Check if telemetry is enabled
       const telemetryEnabled = await this.isTelemetryEnabled();
       if (!telemetryEnabled) {
         this.log('[TelemetryService] Telemetry is disabled, skipping track page view:', pageName);
         return;
       }

       this.log('[TelemetryService] Tracking page view:', pageName, { distinctId, properties });

       // Add current URL to properties
       // Use virtualUrl if provided, otherwise use window.location.href
       const pageUrl = (properties as any).virtualUrl || window.location.href;
       const enrichedProperties = this.injectSessionId({
         ...properties,
         $current_url: pageUrl,
       });

        // Remove virtualUrl from final properties
        delete (enrichedProperties as any).virtualUrl;

        const result = await apiService.trackTelemetryPageView(pageName, distinctId, enrichedProperties);
        if (!result.success) {
         throw new Error(result.error || 'Failed to track page view');
       }

       this.log('[TelemetryService] Page view tracked successfully');
     } catch (error) {
       this.logError('[TelemetryService] Error tracking page view:', error);
     }
   }

   /**
     * Set user properties
     */
   public async setUserProperties(distinctId: string, properties: Record<string, any>): Promise<void> {
     if (!this.initialized) {
       this.logWarn('[TelemetryService] Service not initialized, cannot set user properties');
       return;
     }

     try {
       // Check if telemetry is enabled
       const telemetryEnabled = await this.isTelemetryEnabled();
       if (!telemetryEnabled) {
         this.log('[TelemetryService] Telemetry is disabled, skipping set user properties');
         return;
       }

        const enrichedProperties = this.injectSessionId(properties);

        this.log('[TelemetryService] Setting user properties:', { distinctId, properties: enrichedProperties });

        const result = await apiService.setTelemetryUserProperties(distinctId, enrichedProperties);
        if (!result.success) {
         throw new Error(result.error || 'Failed to set user properties');
       }

       this.log('[TelemetryService] User properties set successfully');
     } catch (error) {
       this.logError('[TelemetryService] Error setting user properties:', error);
     }
   }

  /**
    * Reset telemetry service (useful when user logs out)
    */
  public reset(): void {
    this.log('[TelemetryService] Resetting telemetry service');
    this.initialized = false;
    this.debugMode = false;
    this.telemetryEnabledCache = null;
    this.telemetryEnabledCacheTime = 0;
    this.silentMode = true; // Reset to silent mode
    this.sessionId = null;
  }

  /**
    * Check if telemetry service is initialized
    */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
    * Enable debug mode
    */
  public enableDebug(): void {
    this.debugMode = true;
    this.silentMode = false; // Enable logging in debug mode
    this.log('[TelemetryService] Debug mode enabled');
  }

  /**
    * Disable debug mode
    */
  public disableDebug(): void {
    this.debugMode = false;
    this.silentMode = true; // Disable logging when debug mode is off
    this.log('[TelemetryService] Debug mode disabled');
  }
}

// Export singleton instance
export const telemetryService = TelemetryService.getInstance();
